'use client';

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { dispatchCorrespondenceWorkflowCompleted } from '@/lib/events/correspondence-events';
import type {
  WorkflowData,
  BackendCorrespondence
} from '../correspondences/workflows/workflow-types';
import WorkflowDataService from '../correspondences/workflows/workflow-data.service';
import {
  createCorrespondenceAction,
  getCorrespondenceAction,
  updateCorrespondenceAction,
  sendCorrespondenceAction
} from '../correspondences/actions';
import type { CreateCorrespondenceRequest } from '../correspondences/types';
import {
  correspondenceKeys,
  invalidateAfterCorrespondenceSent
} from '@/lib/query-keys';

interface FollowUpWorkflowProps {
  matterId: number;
  institutionId: number;
  parentCorrespondenceId: number;
}

interface FollowUpWorkflowResult {
  // Draft creation state
  isCreatingDraft: boolean;
  draftCreationError: Error | null;
  correspondenceId: number | null;

  // Correspondence data
  correspondence: BackendCorrespondence | null;
  isLoadingCorrespondence: boolean;
  correspondenceError: Error | null;

  // Workflow data
  workflowData: WorkflowData | null;
  isLoadingWorkflow: boolean;
  workflowError: Error | null;

  // Actions
  updateCorrespondence: (updates: Record<string, unknown>) => Promise<void>;
  sendCorrespondence: (options?: { sendSenderCopy?: boolean }) => Promise<void>;
  refetchData: () => Promise<void>;

  // Send state
  isSending: boolean;
  sendError: Error | null;
}

/**
 * Hook for follow-up correspondence workflow
 * Creates draft on mount and manages workflow data
 */
export function useFollowUpWorkflow({
  matterId,
  institutionId,
  parentCorrespondenceId
}: FollowUpWorkflowProps): FollowUpWorkflowResult {
  const queryClient = useQueryClient();
  const [correspondenceId, setCorrespondenceId] = useState<number | null>(null);
  const [isCreatingDraft, setIsCreatingDraft] = useState(true);
  const [draftCreationError, setDraftCreationError] = useState<Error | null>(
    null
  );

  // Create draft correspondence on mount
  useEffect(() => {
    async function createDraft() {
      try {
        setIsCreatingDraft(true);
        setDraftCreationError(null);

        // Get parent correspondence subject for the follow-up subject
        const parentResult = await getCorrespondenceAction(
          matterId,
          parentCorrespondenceId
        );
        const parentSubject =
          parentResult.success && parentResult.data
            ? parentResult.data.subject
            : 'Previous Correspondence';

        const createRequest: CreateCorrespondenceRequest = {
          type: 'follow-up',
          institutionId,
          parentCorrespondenceId,
          subject: `Follow-up: ${parentSubject}`
        };

        const result = await createCorrespondenceAction(
          matterId,
          createRequest
        );

        if (result.success && result.data) {
          setCorrespondenceId(result.data.id);

          // Invalidate correspondence queries to reflect new draft
          queryClient.invalidateQueries({
            queryKey: correspondenceKeys.forMatter(matterId)
          });
        } else {
          throw new Error(
            result.error || 'Failed to create draft correspondence'
          );
        }
      } catch (error) {
        console.error('Failed to create follow-up draft:', error);
        setDraftCreationError(
          error instanceof Error ? error : new Error('Failed to create draft')
        );
        toast.error('Failed to create follow-up draft. Please try again.');
      } finally {
        setIsCreatingDraft(false);
      }
    }

    createDraft();
  }, [matterId, institutionId, parentCorrespondenceId, queryClient]);

  // Fetch correspondence data once we have the ID
  const {
    data: correspondenceResult,
    isLoading: isLoadingCorrespondence,
    error: correspondenceError,
    refetch: refetchCorrespondence
  } = useQuery({
    queryKey: correspondenceKeys.item(matterId, correspondenceId!),
    queryFn: async () => {
      if (!correspondenceId) return null;
      const result = await getCorrespondenceAction(matterId, correspondenceId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch correspondence');
      }
      return result.data;
    },
    enabled: !!correspondenceId,
    refetchOnWindowFocus: false
  });

  // Fetch workflow data (institution, documents, etc.)
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    error: workflowError,
    refetch: refetchWorkflow
  } = useQuery({
    queryKey: correspondenceKeys.followUpWorkflow(matterId, institutionId),
    queryFn: async () => {
      // Fetch workflow data with specific institution
      const data = await WorkflowDataService.getFollowUpWorkflowData(
        matterId,
        institutionId
      );
      return data;
    },
    enabled: !!correspondenceId, // Only fetch after draft is created
    refetchOnWindowFocus: false
  });

  // Update correspondence mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      if (!correspondenceId) throw new Error('No correspondence ID');

      const result = await updateCorrespondenceAction(
        matterId,
        correspondenceId,
        updates
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to update correspondence');
      }

      return result.data;
    },
    onSuccess: () => {
      // Invalidate correspondence cache
      if (correspondenceId) {
        queryClient.invalidateQueries({
          queryKey: correspondenceKeys.item(matterId, correspondenceId)
        });
      }
    }
  });

  // Send correspondence mutation
  const sendMutation = useMutation({
    mutationFn: async (options?: { sendSenderCopy?: boolean }) => {
      if (!correspondenceId) throw new Error('No correspondence ID');

      const result = await sendCorrespondenceAction(
        matterId,
        correspondenceId,
        options?.sendSenderCopy
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to send correspondence');
      }

      return result.data;
    },
    onSuccess: async () => {
      toast.success('Follow-up correspondence sent successfully');

      // Invalidate all caches affected by sending correspondence
      await invalidateAfterCorrespondenceSent(queryClient, matterId);

      // Refetch the correspondence immediately to get the updated status
      // This will trigger the dialog's useEffect that watches for 'sent' status
      if (correspondenceId) {
        await queryClient.refetchQueries({
          queryKey: correspondenceKeys.item(matterId, correspondenceId)
        });
      }

      // Signal UI (e.g., NextStepsCard) to refresh immediately
      dispatchCorrespondenceWorkflowCompleted({ matterId });
    },
    onError: (error) => {
      console.error('Failed to send correspondence:', error);
      toast.error('Failed to send follow-up. Please try again.');
    }
  });

  // Refetch all data
  const refetchData = useCallback(async () => {
    await Promise.all([refetchCorrespondence(), refetchWorkflow()]);
  }, [refetchCorrespondence, refetchWorkflow]);

  return {
    // Draft creation state
    isCreatingDraft,
    draftCreationError,
    correspondenceId,

    // Correspondence data
    correspondence: correspondenceResult as BackendCorrespondence | null,
    isLoadingCorrespondence,
    correspondenceError: correspondenceError as Error | null,

    // Workflow data
    workflowData: workflowData || null,
    isLoadingWorkflow,
    workflowError: workflowError as Error | null,

    // Actions
    updateCorrespondence: async (updates: Record<string, unknown>) => {
      await updateMutation.mutateAsync(updates);
    },
    sendCorrespondence: async (options?: { sendSenderCopy?: boolean }) => {
      await sendMutation.mutateAsync(options);
    },
    refetchData,

    // Send state
    isSending: sendMutation.isPending,
    sendError: sendMutation.error as Error | null
  };
}
